import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { purposeLabelFromContent, useTranslation } from '../../context/LanguageContext';
import { getContactMailtoHref, submitContactToSheet } from '../../utils/submitContactToSheet';
import './contact-popup.scss';

const ContactPopup = ({ isOpen, onClose, onSubmitSuccess }) => {
  const { content } = useTranslation();
  const pop = content.contact.popup;
  const f = content.contact.form;
  const titleId = useId();
  const firstFieldRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [sheetSaved, setSheetSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failMailto, setFailMailto] = useState('');

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.target;
      const fd = new FormData(form);
      const name = String(fd.get('name') || '').trim();
      const purpose = fd.get('purpose');
      const phone = String(fd.get('phone') || '').trim();
      const message = String(fd.get('message') || '').trim();
      const label = purposeLabelFromContent(content, purpose);

      setSubmitting(true);
      setFailMailto('');
      const result = await submitContactToSheet({ name, phone, topic: label, message });
      setSubmitting(false);

      if (result.ok) {
        form.reset();
        onSubmitSuccess?.();
        setSheetSaved(true);
        return;
      }

      setFailMailto(getContactMailtoHref(content, { name, phone, topic: label, message }));
    },
    [content, onSubmitSuccess]
  );

  useEffect(() => {
    if (!isOpen) {
      setSheetSaved(false);
      setSubmitting(false);
      setFailMailto('');
      return undefined;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!sheetSaved) return undefined;
    const id = window.setTimeout(() => {
      setSheetSaved(false);
      onClose();
    }, 2600);
    return () => window.clearTimeout(id);
  }, [sheetSaved, onClose]);

  if (!isOpen) return null;

  const node = (
    <div className="contact-popup" role="presentation">
      <button
        type="button"
        className="contact-popup__backdrop"
        aria-label="Close contact form"
        onClick={onClose}
      />
      <div
        className="contact-popup__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="contact-popup__head">
          <h2 id={titleId} className="contact-popup__title">
            {pop.title}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="contact-popup__close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <p className="contact-popup__hint">
          {pop.hintBefore}
          <Link className="contact-popup__inline-link" to="/contact" onClick={onClose}>
            {pop.contactPageLink}
          </Link>
          {pop.hintAfter}
        </p>

        {failMailto ? (
          <p className="contact-popup__sheet-error" role="alert">
            {f.sheetFailed}{' '}
            <a className="contact-popup__mailto-link" href={failMailto}>
              {f.emailInstead}
            </a>
          </p>
        ) : null}

        {sheetSaved ? (
          <div className="contact-popup__done" role="status">
            <p className="contact-popup__done-text">{f.sent}</p>
            <button type="button" className="contact-popup__btn contact-popup__btn--primary" onClick={onClose}>
              {pop.closeThanks}
            </button>
          </div>
        ) : (
          <form className="contact-popup__form" onSubmit={handleSubmit}>
          <label className="contact-popup__label" htmlFor="contact-popup-name">
            {pop.name}
          </label>
          <input
            ref={firstFieldRef}
            id="contact-popup-name"
            name="name"
            type="text"
            className="contact-popup__input"
            placeholder={f.namePh}
            autoComplete="name"
            required
            minLength={2}
            maxLength={120}
          />

          <label className="contact-popup__label" htmlFor="contact-popup-phone">
            {pop.phone}
          </label>
          <input
            id="contact-popup-phone"
            name="phone"
            type="tel"
            className="contact-popup__input"
            placeholder={f.phonePh}
            autoComplete="tel"
            required
            minLength={8}
            maxLength={22}
            inputMode="tel"
          />

          <label className="contact-popup__label" htmlFor="contact-popup-purpose">
            {pop.topic}
          </label>
          <select
            id="contact-popup-purpose"
            name="purpose"
            className="contact-popup__select"
            required
            defaultValue=""
          >
            <option value="" disabled>
              {f.topicPlaceholder}
            </option>
            {f.purposeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <label className="contact-popup__label" htmlFor="contact-popup-message">
            {pop.message}
          </label>
          <textarea
            id="contact-popup-message"
            name="message"
            className="contact-popup__textarea"
            rows={4}
            placeholder={pop.messagePh}
            required
            minLength={10}
            maxLength={2000}
          />

          <div className="contact-popup__actions">
            <button
              type="button"
              className="contact-popup__btn contact-popup__btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              {pop.cancel}
            </button>
            <button
              type="submit"
              className="contact-popup__btn contact-popup__btn--primary"
              disabled={submitting}
            >
              {submitting ? f.submitting : pop.submit}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default ContactPopup;
